import { IMindNode } from "../interfaces/IMindNode";
import { Vector } from "../../common/entities/Core/Vector";
import { uuidv4 as uuid } from "../../utils/StringUtils";
import { ITreeNodeForRT } from "../interfaces/ITreeNodeForRT";
import { ITreeNodeForSD } from "../interfaces/ITreeNodeForSD";
import { IRgtf } from "../interfaces/IRgtf";
import { ISelfDev } from "../interfaces/ISelfDev";
import { findNodeInTree, traverseTreeRecursiveAdvanced } from "@/map/utils/TreeUtils";

const DEFAULT_SIZE = new Vector(100, 100);

export type ContentType<T extends object = object> =
    | string
    | HTMLElement
    | HTMLElement[]
    | ((node: T) => string | HTMLElement | HTMLElement[]);

export class MindNode implements IMindNode, ITreeNodeForRT, ITreeNodeForSD {
    id: string; // 节点ID
    parent?: MindNode; // 父节点
    children: MindNode[] = []; // 子节点集合
    position: Vector; // 位置
    size: Vector; // 尺寸
    collapsed: boolean; // 节点是否折叠

    totalChildrenSize?: Vector; // 子节点总尺寸(用于布局计算)
    totalChildrenCount?: number; // 子节点总数量

    data: any = null; // 存储节点自定义数据
    private _content?: ContentType; // 节点内容

    rgTf: IRgtf<MindNode> = {
        depth: 0,       // 节点深度 (层级)
        prelim: 0,      // 初步相对坐标 (水平布局是 y, 垂直布局是 x)
        mod: 0,         // 累积偏移量
        shift: 0,       // 当前子树总位移
        change: 0,      // 子树合并时的位移变化
        thread: undefined, // 指向轮廓上的下一个节点，加速冲突检测
        ancestor: undefined, // 指向当前轮廓的祖先节点
        lmost_sibling: undefined, // 指向最左侧的兄弟节点
        number: 0       // 在同级节点中的索引 (0-based)
    }

    slDv: ISelfDev = {
        maxSubtreeSpan: 0, // 占位最多的下级的占位
        isSizeLargerThanSubtreeSpan: false, // 是否本身高度大于下级的占位
        excessSizeOverSubtreeSpan: 0, // 本身高度大于下级的占位的高度差
    }

    get width(): number { return this.size.x; }
    set width(value: number) { this.size.x = value; }

    get height(): number { return this.size.y; }
    set height(value: number) { this.size.y = value; }

    get x(): number { return this.position.x; }
    set x(value: number) { this.position.x = value; }

    get y(): number { return this.position.y; }
    set y(value: number) { this.position.y = value; }

    get content(): ContentType | undefined {
        return this._content;
    }
    set content(value: ContentType | undefined) {
        this._content = value;
    }

    /**
     * 创建一个思维导图节点实例
     * 
     * @param options - 节点配置选项
     * @param options.id - 节点唯一标识，默认自动生成UUID
     * @param options.position - 节点位置向量，默认为(0,0)
     * @param options.size - 节点尺寸向量，默认为(100,100)
     * @param options.collapsed - 是否折叠节点，默认为false
     * @param children - 子节点列表，默认为空数组
     */
    constructor(
        options: {
            id?: string,
            position?: Vector,
            size?: Vector,
            collapsed?: boolean
        } = {},
        children: MindNode[] = [],
    ) {
        const {
            id = uuid(),
            position = new Vector(0, 0),
            size = DEFAULT_SIZE.clone(),
            collapsed = false // 默认为不折叠
        } = options;

        this.id = id;
        this.position = position;
        this.size = size;
        this.collapsed = collapsed;
        this.children = children;

        this.children.forEach(child => {
            child.parent = this;
        });
    }

    // 添加子节点
    addChild(node: MindNode): void {
        // 防止添加自身
        if (node === this) {
            throw new Error("Cannot add self as child");
        }
        // 防止添加祖先节点(避免循环引用)
        let parent = this.parent;
        while (parent) {
            if (parent === node) {
                throw new Error("Cannot add ancestor as child");
            }
            parent = parent.parent;
        }

        if (node.parent) {
            node.parent.removeChild(node);
        }
        this.children.push(node);
        node.parent = this;
    }

    // 移除子节点
    removeChild(node: MindNode): void {
        const index = this.children.indexOf(node);
        if (index !== -1) {
            this.children.splice(index, 1);
            node.parent = undefined;
        }
    }

    /**
     * 深度优先遍历节点树
     * @param callback - 对每个遍历到的节点执行的回调函数
     * @param {boolean} [skipCollapsedSubtrees=false] - 可选参数，如果为 true，则当遇到折叠的节点时，不会遍历其子树。默认为 false，即遍历所有节点。
     */
    traverse(
        callback: (node: MindNode) => void,
        skipCollapsedSubtrees: boolean = false
    ): void {
        traverseTreeRecursiveAdvanced<MindNode>(
            this,
            (node) => {
                callback(node);
                // 如果需要跳过折叠的子树，并且当前节点是折叠的，则返回 false 来终止该分支的遍历
                if (skipCollapsedSubtrees && node.collapsed) {
                    return false;
                }
            },
            {
                order: 'pre',
                // 仅当 skipCollapsedSubtrees 为 true 时才启用 stopOnFalse
                stopOnFalse: skipCollapsedSubtrees,
            }
        );
    }

    // 查找方法
    findById(id: string): MindNode | null {
        return findNodeInTree<MindNode>(this, (node) => node.id === id);
    }

    // 移动整个子树
    moveTo(newParent: MindNode): void {
        if (this.parent) {
            this.parent.removeChild(this);
        }
        newParent.addChild(this);
    }
}
